/**
 * Starfield Drift Visualizer - Relaxing deep-space parallax starfield
 *
 * A calm ambient starfield with multiple parallax layers, gentle sine-based
 * twinkle, and configurable audio reactivity. The "Audio Reactivity" slider
 * controls how strongly the music influences the scene — from barely-there
 * ambience to punchy star-pulsing on every beat.
 *
 * Rendering layers (bottom to top):
 * 1. Trail fade — offscreen canvas composited at reduced opacity over fresh background
 * 2. Milky Way nebula — pre-rendered offscreen canvas of overlapping radial gradient
 *    clouds along a diagonal band (drawn OUTSIDE trail to avoid accumulation)
 * 3. Stars — parallax-drifting points with per-star twinkle, glow, and diffraction spikes
 *
 * Audio mapping (scaled by audioReactivity):
 * - Bass → star brightness pulse + size breathing + drift speed boost
 * - Treble/high energy → activates extra stars from the pool
 * - Overall energy → modulates twinkle amplitude
 * - Beat detection → brief brightness flash across all stars
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';
import { mapFrequencyData, smoothAmplitudes } from '../lib/audio-utils.js';

// Metadata for manifest generation
export const metadata = {
  name: 'starfield-drift',
  displayName: 'Starfield Drift',
  contextType: '2d',
};

// Maximum pool size — hard cap regardless of config
const MAX_STARS = 800;

// Layer definitions: speed and size multipliers for parallax depth
const LAYER_DEFS = [
  { speed: 0.2, size: 0.5 },   // back — slow, small
  { speed: 0.5, size: 0.75 },  // mid
  { speed: 1.0, size: 1.0 },   // front — fast, large
];

// Color temperature endpoints (cool blue-white → warm amber-white)
const COOL_R = 180, COOL_G = 200, COOL_B = 255;
const WARM_R = 255, WARM_G = 220, WARM_B = 180;

// Milky Way cloud definitions — overlapping ellipses along a diagonal band.
// Three layers: large diffuse background, medium detail, small bright knots.
// Positions are normalised 0–1 relative to canvas dimensions.
const NEBULA_CLOUDS = [
  // Large diffuse background layer — these set the overall shape of the band
  { cx: 0.10, cy: 0.85, rx: 0.25, ry: 0.14, alpha: 0.35 },
  { cx: 0.28, cy: 0.70, rx: 0.30, ry: 0.16, alpha: 0.45 },
  { cx: 0.50, cy: 0.50, rx: 0.35, ry: 0.18, alpha: 0.50 },
  { cx: 0.70, cy: 0.35, rx: 0.30, ry: 0.15, alpha: 0.40 },
  { cx: 0.88, cy: 0.20, rx: 0.24, ry: 0.12, alpha: 0.30 },
  // Medium detail layer — irregular density within the band
  { cx: 0.18, cy: 0.78, rx: 0.14, ry: 0.07, alpha: 0.45 },
  { cx: 0.35, cy: 0.62, rx: 0.16, ry: 0.09, alpha: 0.55 },
  { cx: 0.48, cy: 0.53, rx: 0.18, ry: 0.10, alpha: 0.60 },
  { cx: 0.60, cy: 0.42, rx: 0.15, ry: 0.08, alpha: 0.50 },
  { cx: 0.75, cy: 0.30, rx: 0.14, ry: 0.07, alpha: 0.40 },
  // Small bright knots — dense star-forming regions
  { cx: 0.32, cy: 0.64, rx: 0.07, ry: 0.04, alpha: 0.70 },
  { cx: 0.46, cy: 0.54, rx: 0.08, ry: 0.05, alpha: 0.80 },
  { cx: 0.55, cy: 0.46, rx: 0.06, ry: 0.03, alpha: 0.65 },
  { cx: 0.68, cy: 0.36, rx: 0.07, ry: 0.04, alpha: 0.55 },
];

// Beat flash decay rate per frame (0.92 ≈ fades over ~30 frames / 0.5s)
const BEAT_FLASH_DECAY = 0.92;

class StarfieldDrift extends Base2DVisualizer {
  static name = 'starfield-drift';
  static displayName = 'Starfield Drift';

  static getConfigUI() {
    return [
      { type: 'range', id: 'starCount', label: 'Star Count', min: 100, max: 800, default: 500 },
      { type: 'range', id: 'layers', label: 'Parallax Layers', min: 2, max: 3, default: 3 },
      { type: 'range', id: 'driftSpeed', label: 'Drift Speed', min: 0, max: 100, default: 12, scale: 0.01 },
      { type: 'range', id: 'audioReactivity', label: 'Audio Reactivity', min: 0, max: 100, default: 60, scale: 0.01 },
      { type: 'range', id: 'twinkleStrength', label: 'Twinkle Strength', min: 0, max: 100, default: 40, scale: 0.01 },
      { type: 'range', id: 'twinklePeriod', label: 'Twinkle Period', min: 10, max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'trailAlpha', label: 'Trail Fade', min: 10, max: 80, default: 50, scale: 0.01 },
      { type: 'range', id: 'glowIntensity', label: 'Glow Intensity', min: 0, max: 30, default: 8 },
      { type: 'range', id: 'brightness', label: 'Brightness', min: 20, max: 100, default: 70, scale: 0.01 },
      { type: 'range', id: 'colorTemperature', label: 'Color Temperature', min: 0, max: 100, default: 30, scale: 0.01 },
      { type: 'range', id: 'nebulaBrightness', label: 'Milky Way Brightness', min: 0, max: 200, default: 80, scale: 0.01 },
      { type: 'checkbox', id: 'safeMode', label: 'Clean Mode \u2013 hide Milky Way', default: false },
      { type: 'range', id: 'smoothing', label: 'Smoothing', min: 0, max: 95, default: 85, scale: 0.01 },
    ];
  }

  getElementCount() {
    return 32;
  }

  getDefaultConfig() {
    return {
      colorStart: '#aaccff',
      colorEnd: '#ffeedd',
    };
  }

  init() {
    this.time = 0;
    this.isFirstFrame = true;
    this._beatFlash = 0;

    // Pre-allocate the full star pool
    this.starPool = new Array(MAX_STARS);
    for (let i = 0; i < MAX_STARS; i++) {
      this.starPool[i] = {
        x: 0, y: 0,
        layer: 0,
        baseSize: 0,
        phase: 0,
        twinkleSpeed: 0,
        brightness: 0,
        active: false,
      };
    }

    // Milky Way pre-rendered canvas (built on first draw / resize)
    this._nebulaCanvas = null;
    this._nebulaW = 0;
    this._nebulaH = 0;
    this._nebulaTemp = -1; // force first build

    this._initStars(this.config.starCount);
  }

  /**
   * Initialize or re-initialize stars up to the given count.
   * Stars beyond count are deactivated; new stars get randomized.
   */
  _initStars(count) {
    const w = this.canvas.width || 1920;
    const h = this.canvas.height || 1080;
    const layerCount = this.config.layers;

    for (let i = 0; i < MAX_STARS; i++) {
      const star = this.starPool[i];
      if (i < count) {
        if (!star.active) {
          this._randomizeStar(star, w, h, layerCount, true);
        }
        if (star.layer >= layerCount) {
          star.layer = i % layerCount;
        }
        star.active = true;
      } else {
        star.active = false;
      }
    }

    this._activeCount = count;
  }

  _randomizeStar(star, w, h, layerCount, randomX) {
    star.x = randomX ? Math.random() * w : w + Math.random() * 50;
    star.y = Math.random() * h;
    star.layer = Math.floor(Math.random() * layerCount);
    star.baseSize = 0.5 + Math.random() * 2.0;
    star.phase = Math.random() * Math.PI * 2;
    star.twinkleSpeed = 0.3 + Math.random() * 1.5;
    star.brightness = 0.4 + Math.random() * 0.6;
  }

  onConfigChange(changed) {
    if ('starCount' in changed) {
      this._initStars(changed.starCount);
    }
    if ('layers' in changed) {
      const layerCount = this.config.layers;
      for (let i = 0; i < this._activeCount; i++) {
        if (this.starPool[i].layer >= layerCount) {
          this.starPool[i].layer = i % layerCount;
        }
      }
    }
    // Invalidate nebula cache if color temperature changed
    if ('colorTemperature' in changed) {
      this._nebulaTemp = -1;
    }
  }

  // --- Trail fade infrastructure (from laser-fan-rig.js) ---

  _ensureTrailCanvas() {
    const { canvas } = this;
    if (!this._trailCanvas || this._trailCanvas.width !== canvas.width || this._trailCanvas.height !== canvas.height) {
      if (typeof OffscreenCanvas !== 'undefined') {
        this._trailCanvas = new OffscreenCanvas(canvas.width, canvas.height);
      } else {
        this._trailCanvas = document.createElement('canvas');
        this._trailCanvas.width = canvas.width;
        this._trailCanvas.height = canvas.height;
      }
      this._trailCtx = this._trailCanvas.getContext('2d');
    }
  }

  /**
   * Override draw() to produce trail fade that respects the background controller.
   *
   * Instead of overlaying a semi-transparent solid color (which ignores the global
   * background and always converges to that color), we:
   * 1. Save the current canvas (previous frame) to an offscreen buffer
   * 2. Draw the proper background via clearCanvas() -> drawBackground()
   * 3. Composite the saved frame on top at reduced opacity (trail effect)
   * 4. Render new content
   *
   * This works correctly with solid colors, gradients, patterns, and images.
   */
  draw(frequencyData, features = null) {
    const elementCount = this.getElementCount();

    if (!this._freqBuffer || this._freqBuffer.length !== elementCount) {
      this._freqBuffer = new Array(elementCount).fill(0);
    }

    const raw = mapFrequencyData(frequencyData, elementCount, this._freqBuffer);

    if (this.previousAmplitudes.length !== elementCount) {
      this.previousAmplitudes = new Array(elementCount).fill(0);
    }

    const amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);
    this._currentFeatures = features;

    const { ctx, canvas } = this;

    if (this.isFirstFrame) {
      this.clearCanvas();
      this.isFirstFrame = false;
    } else {
      this._ensureTrailCanvas();

      this._trailCtx.clearRect(0, 0, canvas.width, canvas.height);
      this._trailCtx.drawImage(canvas, 0, 0);

      this.clearCanvas();

      ctx.save();
      ctx.globalAlpha = 1 - this.config.trailAlpha;
      ctx.drawImage(this._trailCanvas, 0, 0);
      ctx.restore();
    }

    // Milky Way is drawn OUTSIDE the trail composite — directly on the fresh
    // canvas after clear+trail, so it never accumulates opacity across frames.
    if (!this.config.safeMode) {
      this._drawMilkyWay(ctx, canvas.width, canvas.height);
    }

    this.renderFrame(amplitudes, features);
  }

  // --- Main rendering ---

  renderFrame(amplitudes, features) {
    const { ctx, canvas, config } = this;
    const w = canvas.width;
    const h = canvas.height;
    const react = config.audioReactivity;

    this.time += 0.016;

    // --- Audio feature extraction ---
    const bassEnergy = this.bassEnergy || 0;
    const highEnergy = this.highEnergy || 0;
    const overallEnergy = this.overallEnergy || 0;

    // --- Beat flash: spike on beat, exponential decay ---
    if (this.beatDetected && react > 0) {
      const confidence = this.beatConfidence || 0.5;
      this._beatFlash = Math.max(this._beatFlash, confidence * react);
    }
    this._beatFlash *= BEAT_FLASH_DECAY;
    if (this._beatFlash < 0.01) this._beatFlash = 0;

    // --- Audio-driven modulations (all scaled by audioReactivity) ---
    // Bass → drift speed boost (up to 3x at full reactivity)
    const driftMultiplier = 1 + bassEnergy * react * 2;
    const effectiveDrift = config.driftSpeed * driftMultiplier;

    // Bass → star brightness pulse
    const bassBrightBoost = bassEnergy * react * 0.5;

    // Bass → star size breathing
    const sizeBoost = 1 + bassEnergy * react * 0.6;

    // Overall energy → twinkle amplitude modulation (±30% at full reactivity)
    const twinkleMod = 1 + (overallEnergy - 0.3) * react * 0.3;
    const effectiveTwinkle = config.twinkleStrength * Math.max(0.7, Math.min(1.3, twinkleMod));

    // High energy → extra stars (up to 80 at full reactivity and high energy)
    const extraStars = Math.floor(highEnergy * react * 80);
    const visibleCount = Math.min(this._activeCount + extraStars, MAX_STARS);

    // Temporarily mark extra stars as active for this frame
    for (let i = this._activeCount; i < visibleCount; i++) {
      const star = this.starPool[i];
      if (!star.active) {
        this._randomizeStar(star, w, h, config.layers, true);
        star.active = true;
        star.brightness *= 0.4;
      }
    }

    // --- Color temperature ---
    const t = config.colorTemperature;
    const baseR = COOL_R + (WARM_R - COOL_R) * t;
    const baseG = COOL_G + (WARM_G - COOL_G) * t;
    const baseB = COOL_B + (WARM_B - COOL_B) * t;

    // --- Star rendering ---
    const glowOn = config.glowIntensity > 0;

    for (let i = 0; i < visibleCount; i++) {
      const star = this.starPool[i];
      if (!star.active) continue;

      const layerDef = LAYER_DEFS[Math.min(star.layer, LAYER_DEFS.length - 1)];

      // --- Drift (horizontal panning with slight vertical wobble) ---
      star.x -= effectiveDrift * layerDef.speed;
      star.y += Math.sin(this.time * 0.3 + star.phase) * 0.05 * layerDef.speed;

      // Wrap around screen edges
      if (star.x < -10) {
        star.x = w + Math.random() * 50;
        star.y = Math.random() * h;
      }
      if (star.x > w + 60) star.x = -5;
      if (star.y < -5) star.y = h + 3;
      if (star.y > h + 5) star.y = -3;

      // --- Twinkle (sine wave per star) ---
      const twinkleValue = Math.sin(this.time * star.twinkleSpeed / config.twinklePeriod + star.phase);
      const baseAlpha = star.brightness * config.brightness
        + twinkleValue * effectiveTwinkle * 0.3
        + bassBrightBoost
        + this._beatFlash * 0.3;
      const alpha = Math.max(0.05, Math.min(1, baseAlpha));

      // --- Size with layer scaling + audio breathing ---
      const size = star.baseSize * layerDef.size * sizeBoost;

      // --- Color with per-star brightness variation ---
      const bri = 0.6 + star.brightness * 0.4;
      const r = Math.min(255, (baseR * bri) | 0);
      const g = Math.min(255, (baseG * bri) | 0);
      const b = Math.min(255, (baseB * bri) | 0);
      const colorStr = `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;

      // --- Draw star ---
      if (glowOn && size >= 1.2) {
        ctx.save();
        ctx.shadowColor = `rgba(${r}, ${g}, ${b}, ${(alpha * 0.5).toFixed(3)})`;
        ctx.shadowBlur = config.glowIntensity * (size / 2);
      }

      ctx.fillStyle = colorStr;

      if (size < 1.0) {
        ctx.fillRect(star.x, star.y, size, size);
      } else if (size < 1.8) {
        ctx.beginPath();
        ctx.arc(star.x, star.y, size, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // Larger stars: circle core + 4-point diffraction spikes
        ctx.beginPath();
        ctx.arc(star.x, star.y, size * 0.7, 0, Math.PI * 2);
        ctx.fill();

        const spikeLen = size * (1.5 + star.brightness);
        const spikeAlpha = alpha * 0.4;
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${spikeAlpha.toFixed(3)})`;
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(star.x - spikeLen, star.y);
        ctx.lineTo(star.x + spikeLen, star.y);
        ctx.moveTo(star.x, star.y - spikeLen);
        ctx.lineTo(star.x, star.y + spikeLen);
        ctx.stroke();
      }

      if (glowOn && size >= 1.2) {
        ctx.restore();
      }
    }

    // Deactivate the extra stars so pool stays clean for next config change
    for (let i = this._activeCount; i < visibleCount; i++) {
      this.starPool[i].active = false;
    }
  }

  // --- Milky Way nebula (pre-rendered offscreen canvas) ---

  /**
   * Build or retrieve the cached Milky Way nebula canvas.
   * Regenerated only on canvas resize or color temperature change.
   * Uses overlapping radial gradient ellipses in three size layers
   * (large diffuse background, medium detail, small bright knots)
   * to create a soft, irregular, nebulous band across the diagonal.
   */
  _ensureNebulaCanvas(w, h) {
    const temp = this.config.colorTemperature;
    if (this._nebulaCanvas && this._nebulaW === w && this._nebulaH === h && this._nebulaTemp === temp) {
      return; // cache hit
    }

    if (typeof OffscreenCanvas !== 'undefined') {
      this._nebulaCanvas = new OffscreenCanvas(w, h);
    } else {
      this._nebulaCanvas = document.createElement('canvas');
      this._nebulaCanvas.width = w;
      this._nebulaCanvas.height = h;
    }
    const nctx = this._nebulaCanvas.getContext('2d');
    nctx.clearRect(0, 0, w, h);

    const t = temp;
    const ri = (COOL_R + (WARM_R - COOL_R) * t) | 0;
    const gi = (COOL_G + (WARM_G - COOL_G) * t) | 0;
    const bi = (COOL_B + (WARM_B - COOL_B) * t) | 0;

    for (let i = 0; i < NEBULA_CLOUDS.length; i++) {
      const cloud = NEBULA_CLOUDS[i];
      const cx = cloud.cx * w;
      const cy = cloud.cy * h;
      const rx = cloud.rx * w;
      const ry = cloud.ry * h;
      const radius = Math.max(rx, ry);

      const grad = nctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
      grad.addColorStop(0, `rgba(${ri}, ${gi}, ${bi}, ${cloud.alpha})`);
      grad.addColorStop(0.3, `rgba(${ri}, ${gi}, ${bi}, ${(cloud.alpha * 0.7).toFixed(4)})`);
      grad.addColorStop(0.6, `rgba(${ri}, ${gi}, ${bi}, ${(cloud.alpha * 0.3).toFixed(4)})`);
      grad.addColorStop(1, 'rgba(0, 0, 0, 0)');

      nctx.save();
      nctx.fillStyle = grad;
      nctx.translate(cx, cy);
      nctx.scale(rx / radius, ry / radius);
      nctx.beginPath();
      nctx.arc(0, 0, radius, 0, Math.PI * 2);
      nctx.fill();
      nctx.restore();
    }

    this._nebulaW = w;
    this._nebulaH = h;
    this._nebulaTemp = temp;
  }

  _drawMilkyWay(ctx, w, h) {
    this._ensureNebulaCanvas(w, h);
    let remaining = this.config.nebulaBrightness;
    // globalAlpha caps at 1.0, so for values > 1 we composite multiple passes
    while (remaining > 0.001) {
      const pass = Math.min(remaining, 1.0);
      ctx.save();
      ctx.globalAlpha = pass;
      ctx.drawImage(this._nebulaCanvas, 0, 0);
      ctx.restore();
      remaining -= pass;
    }
  }

  clear() {
    super.clear();
    this.time = 0;
    this._beatFlash = 0;
    this.isFirstFrame = true;
    if (this._trailCtx && this._trailCanvas) {
      this._trailCtx.clearRect(0, 0, this._trailCanvas.width, this._trailCanvas.height);
    }
  }
}

VisualizerRegistry.register(StarfieldDrift);

export { StarfieldDrift };
export default StarfieldDrift;
